/**
 * Estación elegida en el selector "Cambiar de estación" del panel.
 *
 * Con varias membresías, la web manda `X-Station-Id` en las peticiones del portal
 * (`/api/v1/station/*` y `/api/v1/media/*`); el API siempre la valida contra la membresía. Con una
 * sola membresía no se manda nada: el API usa la estación del evento actual.
 *
 * Vive en `sessionStorage` (solo esta pestaña): sobrevive a recargar la página sin que otra pestaña
 * cambie de estación por debajo. Se guarda junto con el id de la persona: si otra persona ingresa
 * en el mismo navegador, la elección anterior no aplica.
 *
 * Sin dependencias: lo lee el middleware de `src/api/client.ts`.
 */

export const STATION_HEADER = 'X-Station-Id';
export const STATION_SELECTION_KEY = 'parche-cafetero:estacion-activa';

export interface StationChoice {
  userId: string;
  stationId: string;
}

type Listener = () => void;

function defaultStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    // Almacenamiento bloqueado (modo privado antiguo): la elección vive solo en memoria
    return null;
  }
}

function isChoice(value: unknown): value is StationChoice {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.userId === 'string' && typeof candidate.stationId === 'string';
}

export class StationSelection {
  private choice: StationChoice | null;
  private readonly listeners = new Set<Listener>();
  private readonly storage: () => Storage | null;

  constructor(storage: () => Storage | null = defaultStorage) {
    this.storage = storage;
    this.choice = this.read();
  }

  get = (): StationChoice | null => this.choice;

  /** Estación que eligió esa persona (lo que va en `X-Station-Id`), o `null` si no eligió. */
  stationFor = (userId: string | null | undefined): string | null =>
    userId && this.choice?.userId === userId ? this.choice.stationId : null;

  select = (userId: string, stationId: string): void => {
    if (this.choice?.userId === userId && this.choice.stationId === stationId) return;
    this.write({ userId, stationId });
  };

  clear = (): void => {
    if (this.choice === null) return;
    this.write(null);
  };

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private read(): StationChoice | null {
    try {
      const raw = this.storage()?.getItem(STATION_SELECTION_KEY);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      return isChoice(parsed) ? { userId: parsed.userId, stationId: parsed.stationId } : null;
    } catch {
      return null;
    }
  }

  private write(choice: StationChoice | null): void {
    this.choice = choice;
    try {
      const store = this.storage();
      if (choice) store?.setItem(STATION_SELECTION_KEY, JSON.stringify(choice));
      else store?.removeItem(STATION_SELECTION_KEY);
    } catch {
      // Sin almacenamiento: la elección sigue en memoria
    }
    for (const listener of this.listeners) listener();
  }
}

/** Elección única de la app. */
export const stationSelection = new StationSelection();

const STATION_SCOPED = /\/api\/v1\/(station|media)\//;

/** Rutas del API que dependen de la estación activa (las que aceptan `X-Station-Id`). */
export function isStationScoped(pathname: string): boolean {
  return STATION_SCOPED.test(pathname);
}
